import { Download, Languages, X } from "lucide-react";
import React from "react";

interface CanvasHeaderProps {
  selectedLanguage: string;
  currentLevel: number;
  historyCount: number;
  streak?: number;
  onExport: () => void;
  onClose: () => void;
}

const CanvasHeader: React.FC<CanvasHeaderProps> = ({
  selectedLanguage,
  currentLevel,
  historyCount,
  streak = 0,
  onExport,
  onClose,
}) => {
  const exportLabel =
    historyCount === 0
      ? "No sentences to export"
      : `Export ${historyCount} sentence${historyCount === 1 ? "" : "s"}`;

  const currentStreakMod = streak % 5;
  const streakDisplay = currentStreakMod === 0 && streak > 0 ? 5 : currentStreakMod;

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0F0F14]">
      <div className="mx-auto flex min-h-16 max-w-5xl items-center justify-between gap-3 px-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-[#1B1C22]">
            <Languages className="h-4 w-4 text-[#FD954F]" aria-hidden="true" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-medium text-white">{selectedLanguage}</div>
            <div className="text-xs text-white/50">Level {currentLevel}</div>
          </div>

          {streak > 0 && (
            <div
              className="hidden items-center gap-1.5 rounded-full border border-white/10 bg-[#15161B] px-2.5 py-1 sm:flex"
              aria-label={`Commute streak: ${streak} correct in a row`}
              data-testid="commute-streak-indicator"
            >
              <span className="text-xs font-semibold tabular-nums text-[#FD954F]">{streak}</span>
              <span className="text-[11px] text-white/50">in a row</span>
              <div className="ml-1 flex items-center gap-1" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((idx) => (
                  <div
                    key={idx}
                    className={`h-1.5 w-2 rounded-full transition-colors ${
                      idx < streakDisplay ? "bg-[#FD954F]" : "bg-white/20"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onExport}
            disabled={historyCount === 0}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/10 bg-transparent px-3 text-sm font-medium text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
            title={exportLabel}
            aria-label={exportLabel}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={onClose}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/10 bg-[#1B1C22] px-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            <span>Close</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default CanvasHeader;
